import React, { useState, useCallback, useEffect, useMemo } from "react";
import Api from "Api/Api";
import { connect } from "react-redux";
import DataGrid, { Column } from "devextreme-react/data-grid";
import { SelectBox } from "devextreme-react";
import { Input, Pagination } from "antd";
import {
  startupStatus,
  startupStatusMap,
  reverseStartupStatusMap,
} from "formFields";
import moment from "moment";
import { useMatch } from "@reach/router";
// import StartupInvestmentDetails from "components/AdminComponents/StartupInvestmentDetails";
import { StartupInvestmentDetails } from "components/forms/InvestmentDetails";
import { CSVLink } from "react-csv";

const SORT_FIELD_MAP = {
  "startup.name": "name",
  "startup.stage": "stage",
  "startup.ask_amount": "ask_amount",
  "startup.sector": "sector",
  "startup.industry": "industry",
  "startup.segment": "segment",
  status: "status",
  created: "assigned",
};

function InvestorStartupsArrange({ dispatch, formFields }) {
  const match = useMatch("/arrange/:id");
  const { id } = match;
  const [startups, setStartups] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [filters, setFilters] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: null, order: null });

  useEffect(() => {
    if (id) {
      dispatch({
        type: "SectionSelected",
        payload: `Arrange Startups`,
      });
    }
  }, [id, dispatch]);

  const filterHandler = (value) => {
    if (filters) {
      setFilters({ ...filters, ...value });
    } else {
      setFilters(value);
    }
  };

  const handleSearchValue = (value) => {
    const normalized = value || "";
    setSearchTerm(normalized);
    setSearchInput(normalized);
  };

  const startupStatusChanged = (e, startup) => {
    dispatch({ type: "toggleLoad" });
    Api.changeStartupStatus(
      startup.id,
      id,
      reverseStartupStatusMap[e.value]
    ).then((response) => {
      if (response.status === 200) {
        const updateStartups = (records) => records.map((startupRecord) => {
          return startupRecord.id === response.data.id
            ? { ...startupRecord, status: response.data.status }
            : startupRecord;
        });
        const newStartups = updateStartups(startups);
        const newPageData = updateStartups(pageData);
        setStartups(newStartups);
        setPageData(newPageData);
        dispatch({ type: "toggleLoad" });
      }
    });
  };

  const getPageSizeFromUrl = (url) => {
    if (!url) {
      return null;
    }
    try {
      const pageSizeFromUrl = new URL(url).searchParams.get("page_size");
      return pageSizeFromUrl ? Number(pageSizeFromUrl) : null;
    } catch (e) {
      return null;
    }
  };

  const handleSortOrderChange = useCallback(
    (e) => {
      const match = e.fullName?.match(/^columns\[(\d+)\]\.sortOrder$/);
      if (!match) {
        return;
      }
      const columnIndex = Number(match[1]);
      const order = e.value;
      if (!order) {
        setSortConfig({ field: null, order: null });
        return;
      }
      const dataField = e.component.columnOption(columnIndex, "dataField");
      const mappedField = SORT_FIELD_MAP[dataField];
      if (!mappedField) {
        setSortConfig({ field: null, order: null });
        return;
      }
      setSortConfig({ field: mappedField, order });
    },
    [setSortConfig]
  );

  const getStartups = useCallback(
    async (queryFilters = null, page = 1, search = "", sortValue = null) => {
      dispatch({ type: "toggleLoad" });
      const filtersWithSort = sortValue
        ? { ...(queryFilters || {}), sort: sortValue }
        : queryFilters;
      const response = await Api.getStartupFromInvestorID(
        id,
        null,
        filtersWithSort,
        page,
        search
      );
      dispatch({ type: "toggleLoad" });
      if (response.status === 200) {
        const isPaginatedResponse = !Array.isArray(response.data);
        const resultData = isPaginatedResponse
          ? response.data.results || []
          : response.data || [];
        setStartups(resultData);
        setPageData(resultData);
        if (isPaginatedResponse) {
          setCurrentPage(page);
          setTotalRecords(response.data.count || 0);
          const apiPageSize =
            getPageSizeFromUrl(response.data.next) ||
            getPageSizeFromUrl(response.data.previous);
          if (apiPageSize) {
            setPageSize(apiPageSize);
          } else if (page === 1 && resultData.length > 0) {
            setPageSize(resultData.length);
          }
        } else {
          setCurrentPage(1);
          setTotalRecords(resultData.length);
          setPageSize(resultData.length || 10);
        }
      }
    },
    [dispatch, id]
  );

  const sortParam = useMemo(() => {
    if (sortConfig.field && sortConfig.order) {
      return `${sortConfig.field}|${sortConfig.order}`;
    }
    return null;
  }, [sortConfig]);

  useEffect(() => {
    if (id) {
      getStartups(filters, 1, searchTerm, sortParam);
    }
  }, [id, filters, searchTerm, sortParam, getStartups]);

  const csvHeaders = [
    {
      label: 'Name',
      key: 'startup.name'
    },
    {
      label: 'Stage',
      key: 'startup.stage'
    },
    {
      label: 'Sector',
      key: 'startup.sector'
    },
    {
      label: 'Ask amount',
      key: 'startup.ask_amount'
    },
    {
      label: 'Industry',
      key: 'startup.industry'
    },
    {
      label: 'Segment',
      key: 'startup.segment'
    },
    {
      label: 'Last chat description',
      key: 'chat_description'
    },
    {
      label: 'Status',
      key: 'status'
    },
    {
      label: 'Assigned',
      key: 'created'
    },
  ]

  const csvData = useMemo(() => {
    return pageData.map(item => ({
      ...item,
      status: startupStatusMap[item.status] || item.status,
      created: item.created ? moment(item.created).format("LLL") : '',
    }));
  }, [pageData]);

  return (
    <div className="card">
      <div className="card-body">
        <button
          className="btn btn-info mb-3"
          onClick={() => window.history.back()}
        >
          Back
        </button>
        <div className="mb-3">
          <Input.Search
            placeholder="Search startups"
            allowClear
            value={searchInput}
            enterButton
            onSearch={(value) => handleSearchValue(value)}
            onChange={(e) => {
              if (e.target.value === "") {
                handleSearchValue("");
              } else {
                setSearchInput(e.target.value);
              }
            }}
          />
        </div>
        <div
          className="mb-3 grid"
          style={{ gridTemplateColumns: "repeat(5, 1fr)", columnGap: 10 }}
        >
          <SelectBox
            placeholder="Sector"
            dataSource={formFields.sector_choices}
            defaultValue={filters?.sector}
            onValueChanged={(e) => filterHandler({ sector: e.value })}
          />
          <SelectBox
            placeholder="Industry"
            dataSource={formFields.industry_choices}
            defaultValue={filters?.industry}
            onValueChanged={(e) => filterHandler({ industry: e.value })}
          />
          <SelectBox
            placeholder="Segment"
            dataSource={formFields.client_segment_choices}
            defaultValue={filters?.segment}
            onValueChanged={(e) => filterHandler({ segment: e.value })}
          />
          <SelectBox
            placeholder="Stage"
            dataSource={formFields.preferred_stage_choices}
            defaultValue={filters?.stage}
            onValueChanged={(e) => filterHandler({ stage: e.value })}
          />
          <SelectBox
            placeholder="Status"
            dataSource={startupStatus}
            defaultValue={startupStatusMap[filters?.action]}
            onValueChanged={(e) =>
              filterHandler({ action: reverseStartupStatusMap[e.value] })
            }
          />
          <div>
            {!!pageData.length && <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`match_records.csv`}
            >
              Export to CSV
            </CSVLink>}
          </div>
        </div>
        <DataGrid
          dataSource={startups}
          wordWrapEnabled={true}
          hoverStateEnabled={true}
          keyExpr={"startup.id"}
          sorting={{ mode: "single" }}
          onOptionChanged={handleSortOrderChange}
        >
          <Column dataField={"startup.name"} caption="Name" />
          <Column dataField={"startup.stage"} caption="Stage" />
          <Column dataField={"startup.ask_amount"} caption="Ask amount" />
          <Column dataField={"startup.sector"} caption="Sector" />
          <Column dataField={"startup.industry"} caption="Industry" />
          <Column dataField={"startup.segment"} caption="Segment" />
          <Column
            dataField={"chat_description"}
            caption="Last chat description"
          />
          <Column
            dataField={"status"}
            caption={"Status"}
            cellRender={(row) => {
              return (
                <SelectBox
                  dataSource={startupStatus}
                  defaultValue={startupStatusMap[row.data.status]}
                  onValueChanged={(e) =>
                    startupStatusChanged(e, row.data.startup)
                  }
                />
              );
            }}
          />
          <Column
            dataField={"created"}
            caption="Assigned"
            cellRender={(row) => {
              return <span>{moment(row.data.created).format("LLL")}</span>;
            }}
          />
          <Column
            dataField={""}
            caption="Investment detail"
            cellRender={(row) => {
              return (
                <span
                  className="badge badge-pill badge-warning pointer"
                  onClick={() => setSelectedStartup(row.data.startup.id)}
                >
                  Investment details
                </span>
              );
            }}
          />
        </DataGrid>
        <div className="mt-3">
        <Pagination
          current={currentPage}
          total={totalRecords}
          pageSize={pageSize}
          showSizeChanger={false}
          onChange={(page) => getStartups(filters, page, searchTerm, sortParam)}
        />
        </div>
      </div>
      <StartupInvestmentDetails
        id={selectedStartup}
        onClose={() => setSelectedStartup(null)}
        investorId={id}
        dynamicFields={formFields}
      />
      {/* <StartupInvestmentDetails
        id={selectedStartup}
        investorId={id}
        onClose={() => setSelectedStartup(null)}
      /> */}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    formFields: state.formFields,
  };
}
export default connect(mapStateToProps)(InvestorStartupsArrange);
